import type React from 'react';

export type ModuleType =
  | 'listing-summary'
  | 'tour-confirmation'
  | 'amenities'
  | 'neighborhood'
  | 'about-building'
  | 'pets'
  | 'units'
  | 'property-manager'
  | 'cost-and-fees'
  | 'listing-feedback'
  | 'similar-listings'
  | 'zrm-upsell';

type ListingSnapshot =
  | {
      active?: boolean;
      isAllowedToRetain?: boolean;
      trusted?: boolean;
      amenities?: {
        petPolicies?: {
          dog?: unknown;
          cat?: unknown;
        } | null;
      };
    }
  | null
  | undefined;

const canRetainData = (listing: ListingSnapshot): boolean =>
  listing?.active !== false || listing?.isAllowedToRetain !== false;

export type ModuleConfig = {
  id: string;
  type: ModuleType;
  component: React.ComponentType;
  enabled?: boolean;
  title?: string;
  shouldRender?: (listing: ListingSnapshot) => boolean;
};

// Module type constants for easy reordering
export const LISTING_SUMMARY = 'listing-summary' as const;
export const TOUR_CONFIRMATION = 'tour-confirmation' as const;
export const AMENITIES = 'amenities' as const;
export const NEIGHBORHOOD = 'neighborhood' as const;
export const ABOUT_BUILDING = 'about-building' as const;
export const PETS = 'pets' as const;
export const UNITS = 'units' as const;
export const PROPERTY_MANAGER = 'property-manager' as const;
export const COST_AND_FEES = 'cost-and-fees' as const;
export const LISTING_FEEDBACK = 'listing-feedback' as const;
export const SIMILAR_LISTINGS = 'similar-listings' as const;
export const ZRM_UPSELL = 'zrm-upsell' as const;

import { TourConfirmationBanner } from './TourConfirmationModule';
import { NeighborhoodModule } from './NeighborhoodModule/NeighborhoodModule';
import { AboutTheBuildingModule } from './AboutTheBuildingModule';
import { ListingSummaryModule } from './ListingSummaryModule';
import { PetsModule } from './PetsModule';
import { UnitsModule } from './UnitsModule';
import { PropertyManagerModule } from './PropertyManagerModule';
import { CostAndFeesModule } from './CostAndFeesModule';
import { AmenitiesModule } from './AmenitiesModule';
import { ListingFeedbackModule } from './ListingFeedbackModule';
import { SimilarListingsModule } from './SimilarListingsModule';
import ZrmUpsellCarousel from 'app/shared/modules/ZrmUpsellCarousel';

const isInactiveListing = (listing: ListingSnapshot): boolean => listing?.active === false;

// Module definitions lookup
const moduleDefinitions: Record<ModuleType, Omit<ModuleConfig, 'type'>> = {
  [LISTING_SUMMARY]: {
    id: 'listing-summary-module',
    component: ListingSummaryModule,
    enabled: true,
  },
  [TOUR_CONFIRMATION]: {
    id: 'tour-confirmation-module',
    component: TourConfirmationBanner,
    enabled: true,
    title: 'Your booked tour',
  },
  [AMENITIES]: {
    id: 'amenities-module',
    component: AmenitiesModule,
    enabled: true,
    title: 'What you get',
    shouldRender: canRetainData,
  },
  [NEIGHBORHOOD]: {
    id: 'neighborhood-module',
    component: NeighborhoodModule,
    enabled: true,
    title: 'The neighborhood',
  },
  [ABOUT_BUILDING]: {
    id: 'about-building-module',
    component: AboutTheBuildingModule,
    enabled: true,
    title: 'About the building',
    shouldRender: canRetainData,
  },
  [PETS]: {
    id: 'pets-module',
    component: PetsModule,
    enabled: true,
    title: 'Pets',
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      const petPolicies = listing?.amenities?.petPolicies;
      return petPolicies != null && (petPolicies.dog != null || petPolicies.cat != null);
    },
  },
  [UNITS]: {
    id: 'units-module',
    component: UnitsModule,
    enabled: true,
    title: 'Units available in this building',
    shouldRender: (listing) => canRetainData(listing) && listing != null && listing.trusted === true,
  },
  [PROPERTY_MANAGER]: {
    id: 'property-manager-module',
    component: PropertyManagerModule,
    enabled: true,
    title: 'The property manager',
    shouldRender: canRetainData,
  },
  [COST_AND_FEES]: {
    id: 'cost-and-fees-module',
    component: CostAndFeesModule,
    enabled: true,
    title: 'Costs, fees, and lease terms',
    shouldRender: canRetainData,
  },
  [LISTING_FEEDBACK]: {
    id: 'listing-feedback-module',
    component: ListingFeedbackModule,
    enabled: true,
  },
  [SIMILAR_LISTINGS]: {
    id: 'similar-listings-module',
    component: SimilarListingsModule,
    enabled: true,
    title: 'Find similar homes',
    shouldRender: isInactiveListing,
  },
  [ZRM_UPSELL]: {
    id: 'zrm-upsell-module',
    component: ZrmUpsellCarousel,
    enabled: true,
    shouldRender: isInactiveListing,
  },
};

// Simply change the order of constants in this array to reorder modules
const moduleOrder: Array<ModuleType> = [
  LISTING_SUMMARY,
  TOUR_CONFIRMATION,
  SIMILAR_LISTINGS,
  ZRM_UPSELL,
  AMENITIES,
  UNITS,
  COST_AND_FEES,
  PETS,
  NEIGHBORHOOD,
  ABOUT_BUILDING,
  PROPERTY_MANAGER,
  LISTING_FEEDBACK,
];

// Generated configuration based on module order
export const moduleConfig: Array<ModuleConfig> = moduleOrder.map((type) => ({
  type,
  ...moduleDefinitions[type],
}));
